import type { ComponentChildren } from "preact";
import { useId, useRef } from "preact/hooks";
import { cx } from "./lib/cx.ts";

/** How a tab was chosen: a click, or a key that moved the selection. A caller that keeps the tab in the URL replaces
 * the entry on a key move, so stepping through tabs doesn't fill the history. */
export type TabChange = "click" | "key";

export interface TabItem { id: string; label: string; /** A figure after the label, such as how many items the tab holds. */ count?: number | undefined; content: ComponentChildren }

const STEPS = new Map<string, (index: number, count: number) => number>([
  ["ArrowRight", (index) => index + 1], ["ArrowLeft", (index) => index - 1], ["Home", () => 0], ["End", (_index, count) => count - 1],
]);

/**
 * Splits one page into a few parts that are read one at a time. Rule: two to
 * five tabs, named for what they hold; the active tab is a filled pill like a
 * PillGroup, never an underline or a bar. The caller owns the selection (`value`
 * and `onChange`), so it can keep the tab in the URL. Arrow keys, Home and End
 * move between tabs, and `onChange` says which way the tab was chosen. Only the
 * active panel is rendered, so only the active tab names it in aria-controls.
 * Use a PillGroup instead to re-scope one list.
 */
export function Tabs({ label, items, value, onChange, class: className }: {
  label: string;
  items: ReadonlyArray<TabItem>;
  value: string;
  onChange: (id: string, how: TabChange) => void;
  class?: string;
}) {
  const list = useRef<HTMLDivElement>(null);
  // Ids unique to this Tabs, so two on a page, or a tab named like another element, don't collide.
  const base = useId();
  const tabId = (id: string) => `${base}-tab-${id}`;
  const panelId = (id: string) => `${base}-panel-${id}`;
  const active = items.find((item) => item.id === value) ?? items[0];
  const move = (event: KeyboardEvent, index: number) => {
    const step = STEPS.get(event.key);
    if (!step) return;
    event.preventDefault();
    const target = items[(step(index, items.length) + items.length) % items.length];
    if (!target) return;
    onChange(target.id, "key");
    list.current?.querySelector<HTMLElement>(`[data-tab="${target.id}"]`)?.focus();
  };
  return (
    <div class={cx("ui-tabs", className)}>
      <div class="ui-tabs__list" role="tablist" aria-label={label} ref={list}>
        {items.map((item, index) => (
          <button key={item.id} type="button" role="tab" id={tabId(item.id)} data-tab={item.id} class="ui-tab" aria-selected={active?.id === item.id} aria-controls={active?.id === item.id ? panelId(item.id) : undefined}
            tabIndex={active?.id === item.id ? 0 : -1} onClick={() => onChange(item.id, "click")} onKeyDown={(event) => move(event, index)}>
            {item.label}{item.count !== undefined && <span class="ui-tab__count"><span class="ui-sr-only">, </span>{item.count}</span>}
          </button>
        ))}
      </div>
      {active && <div role="tabpanel" id={panelId(active.id)} aria-labelledby={tabId(active.id)} class="ui-tabs__panel" tabIndex={0}>{active.content}</div>}
    </div>
  );
}
